import { RepairOrderRequest, ResponseError, WorkInProgress } from "@/services/api";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogContent,
	DialogSurface,
	DialogTitle,
	DialogTrigger,
} from "@fluentui/react-dialog";
import * as React from "react";
import styles from "./ServiceRequestFormModal.module.scss";
import {
	Field,
	Textarea,
	Button,
	Dropdown,
	Option,
	Label,
	mergeClasses,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { Text } from "@fluentui/react-components";
import { useServiceRequestTypes } from "@/hooks/ServiceRequest";
import { WorkInProgressLineTable } from "../WorkInProgressLineTable/WorkInProgressLineTable";
import { useService } from "@/services/ServiceContext";
import { APIService } from "@/services/APIService/APIService";
import { useSelectedCustomer } from "@/hooks/Customer";
import { useSubmittedRequests, useWipRepairOrders } from "@/hooks/RepairOrders";
import { Spinner } from "@fluentui/react-spinner";
import { addDays, startOfDay } from "date-fns";
import { useIsOnline } from "react-use-is-online";
import { MessageBar } from "../../molecules/MessageBar/MessageBar";
import { toOxfordComma } from "@/helpers/StringHelpers";

export interface IServiceRequestFormModalProps {
	wip?: WorkInProgress | null | undefined;
	onDismiss: () => void;
}

export function ServiceRequestFormModal(props: IServiceRequestFormModalProps) {
	const { isOnline } = useIsOnline();
	const { mutate: mutateRequests } = useSubmittedRequests();
	const { data: requestTypes } = useServiceRequestTypes();
	const { data: customer } = useSelectedCustomer();
	const [submitting, setSubmitting] = React.useState(false);
	const api = useService(APIService.serviceKey);
	const [isOpen, setIsOpen] = React.useState(false);
	const [touched, setTouched] = React.useState(false);
	const { mutate } = useWipRepairOrders(props.wip?.roId);
	const [error, setError] = React.useState<unknown | null>(null);
	const [roRequest, setRoRequest] = React.useState<RepairOrderRequest | null>(null);
	const minDate = React.useMemo(() => startOfDay(new Date()), []);
	React.useEffect(() => {
		if (!!props.wip && !!customer) {
			setIsOpen(true);
			const selectedRO = props.wip;
			const repairOrderRequest: RepairOrderRequest = {
				customerId: customer.id!,
				roId: selectedRO?.roId!,
				vehicleYear: selectedRO?.vehicleYear!,
				vehicleMake: selectedRO?.vehicleMake!,
				vehicleModel: selectedRO?.vehicleModel!,
				vin: selectedRO?.vin!,
				roNumber: selectedRO.roNumber,
				notes: "",
				serviceRequestTypeId: null,
				lines: [],
				requestedDate: startOfDay(addDays(new Date(), 1)),
				licensePlateNum: selectedRO.licensePlateNum,
				licensePlateState: selectedRO.licensePlateState,
				bodyRate: selectedRO.bodyRate,
				condition: selectedRO.condition,
				estimator: selectedRO.estimator,
				exteriorColor: selectedRO.exteriorColor,
				interiorColor: selectedRO.interiorColor,
				mechanicalRate: selectedRO.mechanicalRate,
				odometer: selectedRO.odometer,
				insuranceCompany: selectedRO.insuranceCompany,
				claimNumber: selectedRO.claimNumber,
				lossDate: selectedRO.lossDate,
			};
			setRoRequest(repairOrderRequest);
		}
	}, [props.wip, customer]);

	const srOptions = React.useMemo(() => {
		return requestTypes ?? [];
	}, [requestTypes]);
	const serviceRequest = React.useMemo(() => {
		return srOptions?.find((i) => i.id === roRequest?.serviceRequestTypeId);
	}, [srOptions, roRequest?.serviceRequestTypeId]);

	const closeCallback = React.useCallback(() => {
		props.onDismiss();
		setSubmitting(false);
		setIsOpen(false);
		setRoRequest(null);
		setTouched(false);
		mutate();
		setError(null);
	}, [props, mutate]);

	const submitCallback = React.useCallback(async () => {
		setTouched(true);
		setError(null);
		if (!roRequest || !customer || !serviceRequest) {
			setSubmitting(false);
			return;
		}
		setSubmitting(true);

		try {
			await api.repairOrders.requestRepairOrder({
				repairOrderRequest: roRequest,
			});
			await mutateRequests();
			closeCallback();
		} catch (ex) {
			const re = ex as ResponseError;
			const error = {
				message: await re.response.text(),
				error: re,
			};
			console.error(error);
			setError(error);
			return;
		} finally {
			setSubmitting(false);
		}
	}, [serviceRequest, roRequest, closeCallback, customer, api.repairOrders]);

	const missingFields = React.useMemo(() => {
		const fields: string[] = [];
		if (!roRequest) return fields;

		if (!roRequest.vehicleYear) {
			fields.push("Vehicle Year");
		}
		if (!roRequest.vehicleMake) {
			fields.push("Vehicle Make");
		}
		if (!roRequest.vehicleModel) {
			fields.push("Vehicle Model");
		}
		if (!roRequest.vin) {
			fields.push("VIN");
		}
		return fields;
	}, [roRequest]);

	return (
		<Dialog
			open={isOpen && !!roRequest && !!props.wip}
			modalType="alert"
			onOpenChange={(ev, data) => {
				if (!data.open) {
					closeCallback();
				}
			}}>
			<DialogSurface
				className={mergeClasses(
					styles.dialogRoot,
					!!serviceRequest?.allowLineSelection && styles.mechanical
				)}>
				<DialogBody>
					<DialogTitle>
						<div className={styles.header}>
							<div className={styles.headerElement}>
								<div>RO #</div>
								<div>{roRequest?.roNumber}</div>
							</div>
							<div className={styles.phase}>
								<div>Repair Phase</div>
								<div className={styles.status}> {props.wip?.productionStatus}</div>
							</div>
						</div>
					</DialogTitle>
					<DialogContent>
						<div className={styles.dialogBody}>
							{!!missingFields.length && (
								<div>
									<MessageBar
										messageType="warning"
										onDismiss={() => {
											setError(null);
										}}
										message={
											<div>
												The following properties are missing: {toOxfordComma(missingFields)}.
											</div>
										}></MessageBar>
								</div>
							)}
							{!!error && (
								<div onClick={() => console.log(error)}>
									<MessageBar
										messageType="error"
										dismissable
										hidden={!error}
										onDismiss={() => {
											setError(null);
										}}
										message="We had an issue submitting your request. Please try again. If the issue continues, please contact support for assistance."></MessageBar>
								</div>
							)}
							<div className={styles.vehicleDescription}>
								<div>
									<Label size="large">Vehicle</Label>
									<div>{props.wip?.vehicle}</div>
								</div>
								<div>
									<Label size="large">Vehicle VIN</Label>
									<div>{roRequest?.vin}</div>
								</div>
								<div>
									<Field size="large" required label={"Requested Service Date"}>
										<DatePicker
											minDate={minDate}
											value={roRequest?.requestedDate!}
											disabled={submitting}
											onSelectDate={(date) => {
												console.log(date);
												setRoRequest({ ...roRequest!, requestedDate: date ?? null });
											}}></DatePicker>
									</Field>
								</div>
							</div>
							<div>
								<Field
									label={"Service Request"}
									size="large"
									required
									validationMessage={
										touched && !serviceRequest ? "Select a service request type" : ""
									}>
									<Dropdown
										disabled={submitting}
										selectedOptions={
											!!roRequest?.serviceRequestTypeId ? [roRequest.serviceRequestTypeId] : []
										}
										value={serviceRequest?.name ?? ""}
										onOptionSelect={(ev, opt) => {
											setRoRequest({
												...roRequest!,
												serviceRequestTypeId: opt.optionValue!,
												lines: [],
											});
										}}>
										{srOptions.map((i) => (
											<Option key={i.id} text={i.name!} value={i.id!}>
												{i.name}
											</Option>
										))}
									</Dropdown>
								</Field>
								{!!serviceRequest?.allowLineSelection && (
									<div className={styles.mechanical}>
										<Label size="large">Select RO Lines to add to service Request</Label>
										<WorkInProgressLineTable
											disableSelection={submitting}
											maxHeight={350}
											onSelectionChange={(lines) => {
												const requestout = { ...roRequest!, lines };
												setRoRequest(requestout);
											}}
											lines={props.wip?.lines ?? []}></WorkInProgressLineTable>
									</div>
								)}
								<Field size="large" label={"Notes"}>
									<Textarea
										disabled={submitting}
										value={roRequest?.notes ?? ""}
										onChange={(ev, val) => setRoRequest({ ...roRequest!, notes: val.value ?? "" })}
										size="large"
										resize="vertical"
										rows={8}></Textarea>
								</Field>
							</div>
						</div>
					</DialogContent>
					<DialogActions position="end">
						{submitting && <Spinner size="medium" label="Saving..." />}

						{!submitting && (
							<Button
								appearance="primary"
								disabled={!!missingFields.length || submitting || !isOnline}
								onClick={submitCallback}>
								Submit Request
							</Button>
						)}
						<DialogTrigger disableButtonEnhancement>
							<Button appearance="secondary" disabled={submitting}>
								Close
							</Button>
						</DialogTrigger>
					</DialogActions>
				</DialogBody>
			</DialogSurface>
		</Dialog>
	);
}
